<template>
  <div>
    <oxd-table-filter :filter-title="$t('leave.common_dates_title')">
      <oxd-text tag="p">{{ $t('leave.common_dates_info') }}</oxd-text>
      <oxd-text tag="p">{{ $t('leave.common_dates_limits') }}</oxd-text>
      <oxd-form :loading="loading" @submit-valid="search" @reset="reset">
        <oxd-form-row>
          <employee-autocomplete
            :key="selectionKey"
            :model-value="null"
            :disabled="employees.length >= 100 || loading"
            :params="{includeEmployees: 'onlyCurrent'}"
            @update:model-value="addEmployee"
          />
          <ul class="common-dates-employees">
            <li v-for="employee in employees" :key="employee.id">
              <span>{{ employee.label }}</span>
              <oxd-icon-button
                name="x"
                :aria-label="`${$t('general.remove')} ${employee.label}`"
                :disabled="loading"
                @click="removeEmployee(employee.id)"
              />
            </li>
          </ul>
        </oxd-form-row>
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <date-input
                v-model="fromDate"
                :label="$t('general.from_date')"
                :rules="dateRules"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <date-input
                v-model="toDate"
                :label="$t('general.to_date')"
                :rules="dateRules"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="condition"
                type="select"
                :label="$t('leave.common_dates_condition')"
                :options="conditions"
                :rules="[required]"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-text v-if="error" class="common-dates-error" role="alert">{{
          error
        }}</oxd-text>
        <oxd-divider />
        <oxd-form-actions>
          <oxd-button
            type="reset"
            display-type="ghost"
            :label="$t('general.reset')"
            :disabled="loading"
          />
          <oxd-button
            type="submit"
            display-type="secondary"
            :label="$t('general.search')"
            :disabled="loading"
          />
        </oxd-form-actions>
      </oxd-form>
    </oxd-table-filter>
    <div
      v-if="dates !== null"
      class="orangehrm-paper-container common-dates-results"
      aria-live="polite"
    >
      <oxd-text tag="h6">{{
        $t('leave.common_dates_count', {count: dates.length})
      }}</oxd-text>
      <oxd-text v-if="!dates.length">{{
        $t('leave.common_dates_empty')
      }}</oxd-text>
      <ul v-else>
        <li v-for="date in dates" :key="date.date">
          <oxd-text>{{ date.date }}</oxd-text>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete.vue';
import {APIService} from '@/core/util/services/api.service';
import {required, validDateFormat} from '@/core/util/validation/rules';
import useDateFormat from '@/core/util/composable/useDateFormat';

export default {
  components: {'employee-autocomplete': EmployeeAutocomplete},
  setup() {
    return useDateFormat();
  },
  data() {
    return {
      employees: [],
      selectionKey: 0,
      fromDate: '',
      toDate: '',
      condition: null,
      loading: false,
      error: '',
      dates: null,
    };
  },
  computed: {
    conditions() {
      return ['not_absent', 'school'].map((id) => ({
        id,
        label: this.$t(`leave.common_dates_${id}`),
      }));
    },
    dateRules() {
      return [required, validDateFormat(this.userDateFormat)];
    },
  },
  watch: {
    employees: {
      deep: true,
      handler() {
        this.dates = null;
      },
    },
    fromDate() {
      this.dates = null;
    },
    toDate() {
      this.dates = null;
    },
    condition() {
      this.dates = null;
    },
  },
  methods: {
    required,
    addEmployee(employee) {
      if (!employee?.id) return;
      if (
        this.employees.length < 100 &&
        !this.employees.some((item) => item.id === employee.id)
      )
        this.employees.push(employee);
      this.selectionKey++;
      this.error = '';
    },
    removeEmployee(id) {
      this.employees = this.employees.filter((employee) => employee.id !== id);
    },
    reset() {
      this.employees = [];
      this.fromDate = '';
      this.toDate = '';
      this.condition = null;
      this.error = '';
      this.dates = null;
      this.selectionKey++;
    },
    async search() {
      this.error = '';
      this.dates = null;
      if (!this.employees.length) {
        this.error = this.$t('leave.common_dates_selection_required');
        return;
      }
      const diff = (new Date(this.toDate) - new Date(this.fromDate)) / 86400000;
      if (!Number.isFinite(diff) || diff < 0 || diff > 365) {
        this.error = this.$t('leave.common_dates_invalid_period');
        return;
      }
      this.loading = true;
      try {
        const http = new APIService(
          window.appGlobal.baseUrl,
          '/api/v2/leave/common-dates',
        );
        const {data} = await http.getAll({
          empNumbers: this.employees.map((employee) => employee.id),
          fromDate: this.fromDate,
          toDate: this.toDate,
          condition: this.condition.id,
        });
        this.dates = data.data;
      } catch (e) {
        this.error = this.$t('leave.common_dates_failed');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.common-dates-employees {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0;
  list-style: none;
}
.common-dates-employees li {
  display: flex;
  align-items: center;
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  border-radius: 1rem;
  padding-left: 0.75rem;
}
.common-dates-results {
  padding: 1.5rem;
  margin-top: 1rem;
}
.common-dates-error {
  color: $oxd-feedback-danger-color;
}
</style>
